import * as React from 'react';
import Table from '@mui/material/Table';
import { styled } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { observer } from 'mobx-react-lite';
import SkeltonAnimation from '../../../app/layout/SkeltonAnimation';
import { format } from 'date-fns';
import {
    Box, Card, CardActions, CardContent, CardHeader, Collapse, FormControlLabel, Grid, IconButton, IconButtonProps, Stack,
    Switch,
    Typography, useMediaQuery
} from '@mui/material';
import { useStore } from '../../../app/stores/store';
import { FullCustomerCollection } from '../../../app/models/customer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withNavbar } from '../../../app/layout/HOC';
import { koReportingContainerStyles } from '../koreporting/reports/koReportingContainerStyles';
import { city, companyName, koNumber, country, createDate, cgiRef } from '../../../app/common/options/collectionOpt';
import { mainStyles } from '../../mainStyles';
import { PagingParams } from '../../../app/models/pagination';
import PaginationControlled from '../../../app/common/pagination/PaginationControlled';
import { router } from '../../../app/router/Routes';
import { useEffectOnce } from 'usehooks-ts';
import CollectionReportSearchForm from './forms/CollectionReportSearchForm';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#fcfcfc',
        fontSize: 16,
        fontWeight: 300,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        fontWeight: 300,
        fontStrethed: 'expanded',
    },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#DAE5F1',
    },
    '&:nth-of-type(even)': {
        backgroundColor: '#fcfcfc',
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const gridContainer = {
    xs: 1,
    sm: 2,
    md: 3
} as const;

const textGen = (title: string, value: string, xs: number, sm: number) => {
    return <Grid item xs={xs} sm={sm} key={title}>
        <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
        >
            {title}&nbsp;{value || "---"}
        </Stack>
    </Grid>
}
const iconGen = (title: string, value: boolean, xs: number, sm: number) => {
    return <Grid item xs={xs} sm={sm} key={title}>
        <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
        >
            {title}&nbsp;
            {value === false ? <CloseIcon /> : <DoneIcon />}
        </Stack>
    </Grid>
}

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));


function CollectionReport() {
    const [toggle, setToggle] = useState(false);
    const [reportId, setReportId] = useState<string | null>(null);

    const { basicUserStore: { sortcustomerCollection,
        getCustomerCollectionPagedList, ccPagination, ccPage,
        ccSetPage, setCcPagingParams,
        loadingCustomerCollections, customerCollections,
        setCcSearchPredicate, ccSearchFormValues
    } } = useStore();


    const matches = useMediaQuery('(min-width:900px)');

    useEffectOnce(() => {
        if (customerCollections.length < 1)
            getCustomerCollectionPagedList();
    });

    const handleReportIdCollapse = (pNo: string) => {
        if (reportId === pNo) {
            setReportId(null);
        } else {
            setReportId(pNo);
        }
    };

    const handleCcPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        ccSetPage(value);
        setCcPagingParams(new PagingParams(ccPagination!.currentPage = value));
        getCustomerCollectionPagedList();
    };

    const toggleChangeHandler = (v: boolean) => {
        setToggle(v);
    }

    return (
        <Box component='div' sx={{ mt: 12 }}>
            {loadingCustomerCollections && <SkeltonAnimation iteration={10} />}

            {!loadingCustomerCollections && customerCollections.length < 1 &&
                <Typography variant='h4' textAlign='center' sx={{ mt: 12, color: '#072B02' }} >No records found</Typography>
            }
            {matches && !loadingCustomerCollections && customerCollections.length > 0 &&
                <Stack direction='column' spacing={2}>
                    {toggle ?
                        <CollectionReportSearchForm
                            toggleChangeHandler={toggleChangeHandler}
                            loading={loadingCustomerCollections}
                            setCcSearchPredicate={setCcSearchPredicate}
                            getCustomerCollectionPagedList={getCustomerCollectionPagedList}
                            ccSearchFormValues={ccSearchFormValues}
                        />
                        :
                        <FormControlLabel
                            control={<Switch checked={toggle} color="primary" onChange={() => toggleChangeHandler(true)} name='showCcFilterForm' />}
                            label="Filter"
                            labelPlacement="end"
                        />
                    }
                    <TableContainer sx={koReportingContainerStyles.table}>
                        <Table aria-label="CollectionReport table" stickyHeader={true}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#234f1e' }}>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() =>
                                        sortcustomerCollection(koNumber)}>KoNumber</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() =>
                                        sortcustomerCollection(cgiRef)}>Ref.Nr</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() =>
                                        sortcustomerCollection(companyName)}>Company name</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() =>
                                        sortcustomerCollection(city)}>City</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() =>
                                        sortcustomerCollection(country)}>Country</StyledTableCell>
                                    <StyledTableCell sx={{ cursor: 'pointer' }} onClick={() =>
                                        sortcustomerCollection(createDate)}>Created date</StyledTableCell>
                                    <StyledTableCell>CollectionDate</StyledTableCell>
                                    <StyledTableCell align="center">Received</StyledTableCell>
                                    <StyledTableCell align="center">PreScanned</StyledTableCell>
                                    {/* <StyledTableCell align="center">Ready to Collect</StyledTableCell> */}
                                    <StyledTableCell align="right">Action</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {customerCollections.map((report: FullCustomerCollection, index) => {
                                    return (<StyledTableRow
                                        key={report.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <StyledTableCell>
                                            {report.koNumber || 'Waiting'}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.cgiRefNumber || '--'}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.contactInfo.companyName}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.contactInfo.city}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {report.contactInfo.country}
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ cursor: 'pointer' }} component="th" scope="row">
                                            {format(new Date(report.createdAt), 'yyyy-MM-dd')}
                                        </StyledTableCell>
                                        <StyledTableCell> {report.isDone ? format(new Date(report.collectionDate!), 'yyyy-MM-dd') : '---'}</StyledTableCell>
                                        <StyledTableCell align="center">{report.isGoodReceived || "--"}</StyledTableCell>
                                        <StyledTableCell align="center">{report.isPreScanDone ? <DoneIcon /> : <CloseIcon />}</StyledTableCell>
                                        {/* <StyledTableCell align="center">{report.koNumber && report.singleBox ?
                                        <Tooltip title={report.isReadyToCollect ? "Change to No" : "Change to Yes"}>
                                            <IconButton onClick={() => toggleIsReadyToCollect(report.id)}>
                                                {report.isReadyToCollect ? <DoneIcon /> : <CloseIcon />}
                                            </IconButton>
                                        </Tooltip> : "N/A"}</StyledTableCell> */}
                                        <StyledTableCell align='right'>
                                            <IconButton size='small' onClick={() => router.navigate(`/collectionstatus/${report.id}`)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                            {/* <IconButton size='small' onClick={() => handleOpenCollections(report.id)}>
                                                <VisibilityIcon />
                                            </IconButton> */}
                                        </StyledTableCell>
                                    </StyledTableRow>)
                                })}
                            </TableBody>
                        </Table>
                        <Box sx={{ my: 2 }}>
                            <PaginationControlled loading={loadingCustomerCollections} count={ccPagination!.totalPages} page={ccPage} handleChange={handleCcPageChange} />
                        </Box>
                    </TableContainer>


                </Stack>
            }
            {!matches &&
                <Box sx={{ flexGrow: 1 }} >
                    <Grid container spacing={2} direction="column" justifyContent="center" alignItems="center">
                        {customerCollections.map((report: FullCustomerCollection, index) => {
                            return (
                                <Grid item xs={12} key={report.id}  >
                                    <Card sx={mainStyles.cardStyles}>
                                        <CardHeader
                                            title={report.koNumber || 'Waiting'}
                                            subheader={report.contactInfo.companyName}
                                        />
                                        <CardContent>
                                            <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                                                {textGen('Ref. Nr.', report.cgiRefNumber, 12, 6)}
                                                {textGen('City', report.contactInfo.city, 12, 6)}
                                                {textGen('Country', report.contactInfo.country, 12, 6)}
                                            </Grid>
                                        </CardContent>
                                        <CardActions disableSpacing>
                                            <IconButton size='small' onClick={() => router.navigate(`/collectionstatus/${report.id}`)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                            <ExpandMore
                                                expand={reportId === report.id}
                                                onClick={() => handleReportIdCollapse(report.id)}
                                                aria-expanded={reportId === report.id}
                                                aria-label="More info"
                                            >
                                                <ExpandMoreIcon />
                                            </ExpandMore>
                                        </CardActions>
                                        <Collapse in={reportId === report.id} timeout="auto" unmountOnExit>
                                            <CardContent>
                                                <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={gridContainer}>
                                                    {textGen('Created Date:', format(new Date(report.createdAt), 'yyyy-MM-dd')
                                                        , 12, 6)}
                                                    {textGen('Collection Date:', report.isDone ? format(new Date(report.collectionDate!), 'yyyy-MM-dd') : '---'
                                                        , 12, 6)}
                                                    {textGen('Received:', report.isGoodReceived || "--", 12, 6)}
                                                    {iconGen('Pre Scanned', report.isPreScanDone, 12, 6)}
                                                    {/* {iconGen('Ready to collect', report.isReadyToCollect, 12, 6)} */}
                                                </Grid>
                                            </CardContent>
                                        </Collapse>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                    <Box sx={{ display: 'flex', my: 2, justifyContent: 'center' }}>
                        <PaginationControlled loading={loadingCustomerCollections} count={ccPagination!.totalPages} page={ccPage} handleChange={handleCcPageChange} />
                    </Box>
                </Box>
            }
        </Box>
    );
}
const CollectionReportPage = withNavbar(observer(CollectionReport));
export default CollectionReportPage;
